/* eslint-disable react/prop-types */
import * as React from 'react';

import { Button, Flex, List, Select, Typography } from 'antd';
import type { SelectProps, TreeSelectProps } from 'antd';

import {
  DND_CSS,
  dndArrayMove,
  DndSortableContext,
  useDndSortable,
} from 'context';
import { cn, normalizeToString } from 'utils';

import { CloseOutlinedIcon, MenuOutlinedIcon } from './icon';
import { PrimitiveTreeSelect } from './tree-select';

// NOTE: A labeled value pairs an id (`value`) with its visible `label`, exactly
// the shape antd produces with `labelInValue`/`treeCheckStrictly`. It lets the
// selected items keep their label without re-fetching the source list.
type SortableOption = {
  value?: string | number;
  label?: React.ReactNode;
};

// NOTE: Keeps the previously chosen items in their current order, appends the
// freshly added ones, and drops the removed ones — so adding/removing never
// disturbs the order the user arranged by dragging.
const mergeOrderedOptions = (
  previous: SortableOption[],
  next: SortableOption[],
) => {
  const nextIds = new Set(next.map(({ value }) => normalizeToString(value)));
  const previousIds = new Set(
    previous.map(({ value }) => normalizeToString(value)),
  );

  const retained = previous.filter(({ value }) =>
    nextIds.has(normalizeToString(value)),
  );
  const added = next.filter(
    ({ value }) => !previousIds.has(normalizeToString(value)),
  );

  return [...retained, ...added];
};

type SortableSelectedItemProps = {
  option: SortableOption;
  onRemove: (id: string) => void;
};

const SortableSelectedItem = ({
  option,
  onRemove,
}: SortableSelectedItemProps) => {
  const id = normalizeToString(option.value);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDndSortable({ id });

  return (
    <List.Item
      ref={setNodeRef}
      className='gap-2 rounded border bg-slate-50 px-3'
      style={{
        transform: DND_CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
      }}
      actions={[
        <Button
          key='remove'
          danger
          type='text'
          size='small'
          icon={<CloseOutlinedIcon />}
          onClick={() => onRemove(id)}
        />,
      ]}
    >
      <Flex align='center' gap='small' className='min-w-0 flex-1'>
        <MenuOutlinedIcon
          className='shrink-0 cursor-grab text-[--ant-color-text-tertiary] active:cursor-grabbing'
          {...attributes}
          {...listeners}
        />
        <Typography.Text ellipsis={{ tooltip: true }}>
          {option.label}
        </Typography.Text>
      </Flex>
    </List.Item>
  );
};

type SortableSelectedListProps = {
  value: SortableOption[];
  onChange: (value: SortableOption[]) => void;
};

const SortableSelectedList = ({
  value,
  onChange,
}: SortableSelectedListProps) => {
  if (!value.length) return <React.Fragment />;

  return (
    <DndSortableContext
      dnd={{
        onDragEnd: ({ active, over }) => {
          const overId = over?.id;
          if (!overId || active.id === overId) return;

          const activeIndex = value.findIndex(
            ({ value }) => normalizeToString(value) === active.id,
          );
          const overIndex = value.findIndex(
            ({ value }) => normalizeToString(value) === overId,
          );
          if (activeIndex < 0 || overIndex < 0) return;

          onChange(dndArrayMove(value, activeIndex, overIndex));
        },
      }}
      sortable={{
        items: value.map(({ value }) => normalizeToString(value)),
        children: (
          <List
            size='small'
            className='mt-2 [&_.ant-list-items]:flex [&_.ant-list-items]:flex-col [&_.ant-list-items]:gap-1.5'
            dataSource={value}
            renderItem={option => (
              <SortableSelectedItem
                option={option}
                onRemove={id =>
                  onChange(
                    value.filter(item => normalizeToString(item.value) !== id),
                  )
                }
              />
            )}
          />
        ),
      }}
    />
  );
};

type PrimitiveSortableSelectProps = {
  value?: SortableOption[];
  onChange?: (value: SortableOption[]) => void;
  options?: SelectProps['options'];
  loading?: boolean;
  placeholder?: string;
  className?: string;
};

const PrimitiveSortableSelect = ({
  value = [],
  onChange,
  options,
  loading,
  placeholder,
  className,
}: PrimitiveSortableSelectProps) => (
  <div className={cn(className)}>
    <Select<SortableOption[]>
      labelInValue
      mode='multiple'
      maxTagCount={0}
      maxTagPlaceholder={omittedValues =>
        `${omittedValues.length} مورد انتخاب شده`
      }
      className='w-full'
      placeholder={placeholder}
      loading={loading}
      options={options}
      value={value}
      filterOption={(inputValue, option) =>
        normalizeToString(option?.label)
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      }
      onChange={next => onChange?.(mergeOrderedOptions(value, next))}
    />
    <SortableSelectedList value={value} onChange={onChange ?? (() => {})} />
  </div>
);

type PrimitiveSortableTreeSelectProps = {
  value?: SortableOption[];
  onChange?: (value: SortableOption[]) => void;
  treeData?: TreeSelectProps['treeData'];
  loading?: boolean;
  placeholder?: string;
  className?: string;
};

const PrimitiveSortableTreeSelect = ({
  value = [],
  onChange,
  treeData,
  loading,
  placeholder,
  className,
}: PrimitiveSortableTreeSelectProps) => (
  <div className={cn(className)}>
    <PrimitiveTreeSelect<SortableOption[]>
      multiple
      treeCheckable
      treeCheckStrictly
      maxTagCount={0}
      maxTagPlaceholder={omittedValues =>
        `${omittedValues.length} مورد انتخاب شده`
      }
      className='w-full'
      placeholder={placeholder}
      loading={loading}
      treeData={treeData}
      value={value}
      onChange={next => onChange?.(mergeOrderedOptions(value, next))}
    />
    <SortableSelectedList value={value} onChange={onChange ?? (() => {})} />
  </div>
);

export { PrimitiveSortableSelect, PrimitiveSortableTreeSelect };
